import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { TenantContextService } from 'app/@core/services/utility/tenant-context.service';
import { firstValueFrom } from 'rxjs';

/**
 * Ensures the authenticated user has a valid tenantId in their ID token claims
 * before granting access to protected routes.
 *
 * This guard runs AFTER AuthGuard (which verifies Firebase authentication).
 * It guards against the edge case where a user is signed in but has not yet
 * been assigned to a tenant (e.g. the onAppUserCreated trigger hasn't fired,
 * or the user was created without tenantId/role fields).
 *
 * On failure: signs the user out and redirects to /sign-in.
 * This is a deliberate hard stop — a user without tenant context must not
 * access any tenant-scoped data.
 *
 * Route wiring:
 *   {
 *     path: '',
 *     canActivate: [AuthGuard, TenantGuard],
 *     canActivateChild: [AuthGuard, TenantGuard],
 *     ...
 *   }
 */
export const TenantGuard: CanActivateFn | CanActivateChildFn = async () => {
    const tenantCtx = inject(TenantContextService);
    const router = inject(Router);

    // `claims$` uses shareReplay(1), so this resolves immediately if the
    // observable has already emitted (i.e. after login), or waits for the
    // first emission if the guard runs before the token has been decoded.
    const claims = await firstValueFrom(tenantCtx.claims$);

    if (!claims?.tenantId) {
        // Redirect to sign-in. The user can log in again once an admin has
        // assigned them to a tenant via assignUserToTenant.
        return router.parseUrl('sign-in');
    }

    return true;
};
